import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDynamicLocale } from '@/hoslog-intl';
import { z } from 'zod';
import useManageSlab from './hooks/useManageSlab';

const type = {
  PERCENTAGE: 'P',
  FLAT: 'F',
};
const manageCommissionSlabsFormSchema = z
  .object({
    categoryName: z.string(),
    fromAmount: z.string().min(1, { message: 'common.form.validation.required' }),
    toAmount: z.string().min(1, { message: 'common.form.validation.required' }),
    commissionPercentageType: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    commissionValue: z.string().min(1, { message: 'common.form.validation.required' }),
    minCommissionValue: z.string().min(1, { message: 'common.form.validation.required' }),
    maxCommissionValue: z.string().min(1, { message: 'common.form.validation.required' }),
  })
  .superRefine((data, context) => {
    const fromAmount = Number(data.fromAmount);
    const toAmount = Number(data.toAmount);
    const commissionValue = Number(data.commissionValue);
    const minCommissionValue = Number(data.minCommissionValue);
    const maxCommissionValue = Number(data.maxCommissionValue);
    if (data.fromAmount && data.toAmount && fromAmount >= toAmount) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['fromAmount'],
      });
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'common.form.validation.invalid', path: ['toAmount'] });
    }
    if (data.commissionPercentageType === type.PERCENTAGE && commissionValue > 100) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['commissionValue'],
      });
    }
    if (data.minCommissionValue && data.maxCommissionValue && minCommissionValue >= maxCommissionValue) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['minCommissionValue'],
      });
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['maxCommissionValue'],
      });
    }
  });

export const ManageCommissionSlab = ({
  defaultValues,
}: {
  defaultValues: {
    categoryId: string;
    adminCommissionTypeId: string;
    categoryName: string | undefined;
    categoryDetailId: string;
    fromAmount: string;
    toAmount: string;
    commissionValue: string;
    commissionPercentageType: string;
    minCommissionValue: string;
    maxCommissionValue: string;
  };
}) => {
  const form = useForm({
    schema: manageCommissionSlabsFormSchema,
    mode: 'onBlur',
    defaultValues,
  });

  const { mutateAsync } = useManageSlab();

  const { t } = useDynamicLocale();
  const commissionTypeDropdown = [
    { label: t('configurationManagement.commissions.table.title.flat'), value: 'F' },
    { label: t('configurationManagement.commissions.table.title.percentage'), value: 'P' },
  ];

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        await mutateAsync({
          categoryId: defaultValues.categoryId || '',
          adminCommissionTypeId: defaultValues.adminCommissionTypeId || '',
          categoryName: data.categoryName,
          categoryDetailId: defaultValues.categoryDetailId || '',
          fromAmount: Number(data.fromAmount),
          toAmount: Number(data.toAmount),
          commissionValue: Number(data.commissionValue),
          commissionPercentageType: data.commissionPercentageType,
          minCommissionValue: Number(data.minCommissionValue),
          maxCommissionValue: Number(data.maxCommissionValue),
        });
      }}
    >
      <div className="w-full flex flex-col gap-4.5">
        <Input
          label="configurationManagement.commissions.manageSlabsModal.categoryName"
          {...form.register('categoryName')}
          disabled
          placeholder={t('configurationManagement.commissions.manageSlabsModal.categoryName')}
          forceRequired
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            inputMode="numeric"
            type="text"
            onlyNumbers
            maxLength={10}
            label="configurationManagement.commissions.manageSlabsModal.fromAmount"
            {...form.register('fromAmount')}
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterAmount')}
            forceRequired
          />
          <Input
            label="configurationManagement.commissions.manageSlabsModal.toAmount"
            {...form.register('toAmount')}
            type="text"
            onlyNumbers
            maxLength={10}
            inputMode="numeric"
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterAmount')}
            forceRequired
          />
        </div>
        <Select
          options={commissionTypeDropdown}
          label="configurationManagement.commissions.manageSlabsModal.categoryType"
          placeholder={t('configurationManagement.commissions.crumb.commissionType')}
          {...form.register('commissionPercentageType')}
          forceRequired
        />

        <Input
          label="configurationManagement.commissions.manageSlabsModal.value"
          {...form.register('commissionValue')}
          placeholder={t('configurationManagement.commissions.manageSlabsModal.enterValue')}
          type="text"
          onlyNumbers
          maxLength={10}
          forceRequired
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="configurationManagement.commissions.manageSlabsModal.minValue"
            {...form.register('minCommissionValue')}
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMinValue')}
            type="text"
            onlyNumbers
            maxLength={10}
            forceRequired
          />
          <Input
            label="configurationManagement.commissions.manageSlabsModal.maxValue"
            {...form.register('maxCommissionValue')}
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMaxValue')}
            type="text"
            onlyNumbers
            maxLength={10}
            forceRequired
          />
        </div>
      </div>
    </Form>
  );
};
